(function () {
  if (customElements.get('predictive-search')) return;

  class PredictiveSearch extends window.SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      // CFSN-670: switchOption() reads this before the first real search ever runs
      // (zero-state arrow-key navigation) — must exist from the start, not just lazily
      // via setLiveRegionLoadingState() once a query is typed.
      this.statusElement = this.querySelector('.predictive-search-status');

      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      // CFSN-670: the inherited "input" handler is debounced by 300ms (search-form.js). Drop the
      // zero-state selection and its stale markup right away instead — typing fast and hitting
      // Enter inside that window would otherwise still open the previously selected term.
      this.input.addEventListener('input', this.onImmediateInput.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    // CFSN-670: runs on every keystroke, before the debounced onChange(). A new character
    // invalidates both the zero-state selection (which would otherwise hijack Enter/submit or
    // make arrow navigation compute an out-of-range index) and the injected zero-results block
    // including its "no results" message, which must not linger while the next query loads.
    onImmediateInput() {
      this.clearSelection();
      this.removeInjectedZeroState();
    }

    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();

      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      // Update the term asap, don't wait for the predictive search query to finish loading
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);

      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    // CFSN-670: only a selection that is actually rendered counts. A leftover selection on the
    // hidden zero-state must never be treated as the active option.
    visibleMatch(selector) {
      return Array.from(this.querySelectorAll(selector)).find((element) => element.offsetParent !== null);
    }

    // CFSN-670: drop any selection state, including leftovers on hidden zero-state copies.
    clearSelection() {
      this.querySelectorAll('[aria-selected="true"]').forEach((element) =>
        element.setAttribute('aria-selected', 'false')
      );
      this.input.setAttribute('aria-activedescendant', '');
    }

    // CFSN-670: the zero-results fallback is injected into the results container and would
    // otherwise linger there — as a hidden stale copy after a reset, or as an outdated "no
    // results" message while the next query loads. The wrapper holds message and
    // recommendations together, so both always go at once.
    removeInjectedZeroState() {
      this.predictiveSearchResults
        .querySelectorAll('[data-search-zero-state-empty], [data-search-zero-state]')
        .forEach((element) => element.remove());
    }

    // CFSN-670: the header zero-state is a second, CSS-toggled listbox. Keep the combobox
    // pointed at whichever listbox is actually visible, instead of always at the results one.
    syncZeroStateAria() {
      const zeroState = this.querySelector('[data-search-zero-state][id]');

      if (zeroState && zeroState.offsetParent !== null) {
        this.input.setAttribute('aria-expanded', 'true');
        this.input.setAttribute('aria-controls', zeroState.id);
        this.input.setAttribute('aria-owns', zeroState.id);
        return;
      }

      this.input.setAttribute('aria-controls', 'predictive-search-results');
      this.input.setAttribute('aria-owns', 'predictive-search-results');
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.visibleMatch('[aria-selected="true"] a')) event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      // CFSN-670: the zero-state is already expanded when the modal opens, so the combobox has
      // to say so — open() only runs once a query exists.
      this.syncZeroStateAria();

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      // CFSN-670: don't close/reset on navigation keys while the query is empty, otherwise
      // this wipes the zero-state's arrow-key selection before switchOption()/selectOption() can use it.
      if (!this.getQuery().length && !['ArrowUp', 'ArrowDown', 'Enter'].includes(event.code)) this.close(true);
      event.preventDefault();

      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        event.preventDefault();
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
      const currentButtonText = searchForTextElement?.innerText;
      if (currentButtonText) {
        if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
          // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
          return;
        }
        const newButtonText = currentButtonText.replace(previousTerm, newTerm);
        searchForTextElement.innerText = newButtonText;
      }
    }

    switchOption(direction) {
      // CFSN-670: the zero-state is visible before any query is typed, when "open" is never set
      // (onFocus() only calls open() once there's a query). Allow arrow-key navigation there too.
      const zeroStateVisible = !this.hasAttribute('results') && this.visibleMatch('[data-search-zero-state]');
      if (!this.getAttribute('open') && !zeroStateVisible) return;

      const moveUp = direction === 'up';
      const selectedElement = this.visibleMatch('[aria-selected="true"]');

      // Filter out hidden elements (duplicated page and article resources) thanks
      // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
      const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
        (element) => element.offsetParent !== null
      );
      let activeElementIndex = 0;

      // CFSN-670: nothing rendered to navigate — bail out instead of indexing an empty list.
      if (!allVisibleElements.length) return;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      this.statusElement.textContent = '';

      if (selectedElementIndex === -1) {
        // CFSN-670: the selection is not among the rendered options (e.g. a zero-state leftover).
        // Start over instead of computing a negative index, which used to crash on ArrowUp.
        activeElementIndex = moveUp ? allVisibleElements.length - 1 : 0;
      } else if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];
      if (!activeElement) return;

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.visibleMatch('[aria-selected="true"] a, button[aria-selected="true"]');

      if (selectedOption) selectedOption.click();
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
        signal: this.abortController.signal,
      })
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search').innerHTML;
          // Save bandwidth keeping the cache in all instances synced
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          if (error?.code === 20) {
            // Code 20 means the call was aborted
            return;
          }
          this.close();
          throw error;
        });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.scopeZeroStateIds();
      this.setAttribute('results', true);

      this.setLiveRegionResults();
      this.open();

      // CFSN-670: results replaced the zero-state — point the combobox back at the results listbox.
      this.syncZeroStateAria();

      updateProductGridDiscounts(this);

    }

    // CFSN-670: the zero-results fallback is fetched via a shared AJAX section that
    // doesn't know which <predictive-search> instance triggered it, so its ids/aria-labelledby
    // aren't unique when more than one instance is on the page. Scope them here using this
    // widget's own (already-unique) input id.
    scopeZeroStateIds() {
      const zeroState = this.predictiveSearchResults.querySelector('[data-search-zero-state]');
      if (!zeroState || !this.input.id) return;

      zeroState.querySelectorAll('[id]').forEach((element) => {
        element.id = `${element.id}-${this.input.id}`;
      });
      zeroState.querySelectorAll('[aria-labelledby]').forEach((element) => {
        element.setAttribute('aria-labelledby', `${element.getAttribute('aria-labelledby')}-${this.input.id}`);
      });
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }

    getResultsMaxHeight() {
      this.resultsMaxHeight =
        window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }

    open() {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
        // CFSN-670: the injected zero-results fallback must not survive a reset, otherwise it
        // stays behind as a hidden duplicate of the header zero-state.
        this.removeInjectedZeroState();
      }

      // CFSN-670: clear every selection, not just the first one — hidden zero-state leftovers
      // would otherwise keep hijacking Enter/submit and arrow navigation.
      this.clearSelection();

      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.removeAttribute('style');

      // CFSN-670: after removing "results" the header zero-state becomes visible again — point
      // the combobox ARIA at whichever listbox is actually on screen now.
      this.syncZeroStateAria();
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
